/*
 * ErrorBoundary — wraps a subtree and renders a fallback UI on any render error.
 * Prevents white-screening the whole dashboard when a single panel throws
 * (e.g. Leaflet tile fetch fail, malformed coordinates, transient JSON error).
 */
import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error("[ErrorBoundary]", this.props.name || "panel", error, info);
  }

  reset = () => this.setState({ hasError: false, error: null });

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback(this.state.error, this.reset);
    return (
      <div
        className="border border-red-200 bg-red-50 rounded-xl p-6 text-center"
        data-testid={`error-boundary-${this.props.name || "panel"}`}
      >
        <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
        <div className="text-sm font-semibold text-red-800 mb-1">
          Something went wrong loading {this.props.name || "this panel"}.
        </div>
        <div className="text-xs text-red-700 mb-4 break-words">
          {this.state.error?.message || "Unknown error"}
        </div>
        <button
          onClick={this.reset}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-red-600 text-white text-sm font-semibold"
        >
          <RefreshCw className="w-4 h-4" /> Try again
        </button>
      </div>
    );
  }
}
